// [document rules] THE placeholder reader: every blank, all-caps label, drafter
// note, {{x}} / {x} / <<x>> token and bracketed span is recognised here and
// nowhere else (documentRules/README via RULES.md; lint rule 16). The template
// tagger's patterns below are the source of truth; the draft-side scans at the
// bottom reuse them. Moved verbatim from services/templateTagging.ts, which
// re-exports it.
//
// [template-tagging] Phase 1 of preparing a certified template for filling:
// everything that can be decided WITHOUT a model.
//
// WHY THERE IS A DETERMINISTIC PHASE AT ALL. Measured on the certified corpus,
// asking a model to find a template's variables produced 82 of them where a
// lawyer would name ~11, and the published CUAD figures explain why prompting
// cannot fix that: definition extraction scores ~97% F1 per item but ~76% at the
// whole-contract level, because per-item error compounds across a document. The
// lever is fewer model decisions, not better ones. This module removes about
// half of them.
//
// WHAT IT CLAIMS, and why each rule is safe without judgement:
//
//   _______ ("Company")   the contract NAMES the slot itself. A defined term has
//                         exactly one meaning throughout — that is the point of
//                         declaring it — so this is a lookup, not a guess.
//   Tel: _______          a short label immediately before a gap. This corpus
//                         writes labelled fields that way throughout.
//   [COMPANY NAME]        an all-caps bracket IS the slot.
//   [for up to \t weeks]  the gap is a literal tab. Scanning only for `_{3,}`
//                         missed these and lost correct values that had nowhere
//                         to land.
//   $100 per diem         money in the body of a performer agreement is a
//                         negotiated number, never legal machinery. This is the
//                         only "already has a value" pattern confident enough to
//                         take without a model, and it is the one that matters:
//                         a hardcoded default is invisible to any blank-scanner,
//                         so nothing downstream could see it at all.
//   [*Check with the...]  an asterisk-led bracket is an instruction to the
//                         drafter. It is deleted, never filled.
//
// WHAT IT DELIBERATELY LEAVES ALONE. Percentages, day counts and mile radii are
// NOT claimed here, because this corpus writes deal terms and fixed legal
// machinery in identical syntax:
//
//   35% of the size of type used for the title   a credit term, negotiable
//   further than 50 miles from Artist's residence   distant-location, fixed
//
// A regex that guessed between those would eventually tag the second kind, and a
// wrongly tagged span invites a drafter to negotiate something that is not
// negotiable. Abstaining is the correct behaviour: those go to the model phase,
// which can reason about them, on a document with everything below already
// masked out.
import { htmlToText } from "../../services/reviewEngineV2/segment.js";
import { CLAUSE_NUMBER, blocksOf, conventionText } from "./blocks.js";
import { OPTION_SPAN } from "./options.js";
import { drafterNotesIn, opensAsNote } from "./notes.js";
const BLANK_RE = /_{3,}/g;
const BRACKET_RE = /\[[^\][]{1,240}\]/g;
const LABEL_RE = /^\[[A-Z][A-Z0-9 /&'-]{2,40}\]$/;
const GAP_IN_BRACKET = /_+|\t+/;
const DECL_AFTER = /^[^_]{0,30}\(\s*(?:the\s+|each\s+an?\s+|an?\s+)?"([^"]{2,60})"\s*\)/;
const MONEY_RE = /\$\s?[\d,]+(?:\.\d{2})?(?:\s*per\s*(?:diem|day|week))?/g;
/** A short run of words ending at the gap — how this corpus labels its fields.
 *  Kept narrow on purpose: anything it does not match is left unnamed for the
 *  model phase rather than given a name invented from nearby prose. */
const FUZZY_LABEL = /([A-Za-z][A-Za-z'-]{1,14}(?:\s+[a-z][A-Za-z'-]{1,14}){0,2})\s*[:.]?\s*$/;
/** Words that carry no name on their own. Run across the certified corpus the
 *  bare label rule produced `<and>`, `<by_and_between>` and `<of_that_certain>`
 *  — the connective tissue a contract uses to introduce a party, not a label for
 *  the value. A slot named `and` is worse than an unnamed one, because a human
 *  reviewer skims past a plausible-looking name and stops at `unnamed_7`. */
const STOPWORDS = new Set([
    "a", "an", "and", "as", "at", "between", "by", "certain", "for", "from", "in",
    "is", "of", "on", "or", "that", "the", "this", "to", "up", "with",
]);
/** A label survives only if it carries at least one word that is not connective. */
function isUsableLabel(label) {
    const words = label.toLowerCase().split(/[\s'-]+/).filter((w) => w.length > 0);
    return words.length > 0 && words.some((w) => !STOPWORDS.has(w));
}
/** A blank's label is usually in front of it, but not always: this corpus also
 *  writes "[, in __________ position of all cast members]" and "[for up to ____
 *  consecutive weeks of principal photography]", where everything in front is
 *  connective ("in", "for up to") and the noun follows the gap.
 *
 *  Measured on tpl-a7fc6bc79eae0c1a: those two blanks were found, could not be
 *  named, and were REMOVED at review as `unnamed_8` and `unnamed_11` — taking
 *  with them the only place a billing position or a photography schedule could
 *  ever land. A deal supplying both then reports them as terms the template has
 *  no home for, which was not true.
 *
 *  A modal in front means the words after the gap are a predicate, not a label
 *  ("______ shall pay the Artist"), and a name invented from those is worse
 *  than no name at all. */
const MODALS = new Set(["shall", "will", "would", "may", "must", "can", "is", "are", "was", "were", "has", "have", "had", "agrees", "acknowledges"]);
function usableLabelAfter(text, end) {
    // A clause that closes ends the label with it: "[for up to ____ consecutive
    // weeks] thereafter" is named for the weeks, not for what follows the bracket.
    const window = text.slice(end, end + 40).split(/[\])};.]/)[0] ?? "";
    // A FORM BLOCK captions the gap that FOLLOWS it, not the one before:
    //
    //    Artist: ______ ______ ______ Tel: ______ Fax: ______
    //    [COMPANY NAME] ______ Signature ______ Print Name ______
    //
    // Reading forwards there would name the third address line "tel" and shift
    // every name in the block by one, so a filler would write the phone number
    // into an address. Another gap close behind means these words belong to it;
    // a caption ending in ":" says the same thing outright. Only a gap inside a
    // running sentence takes its name from what follows it.
    if (/_{3,}/.test(window))
        return null;
    const after = window.replace(/^[\s,.;:)\]]+/, "");
    if (/^[A-Za-z][A-Za-z'-]*\s*:/.test(after))
        return null;
    const words = after.split(/[\s]+/).filter((w) => w.length > 0);
    const taken = [];
    for (const raw of words.slice(0, 3)) {
        const w = raw.replace(/[^A-Za-z'-]/g, "");
        if (w.length === 0)
            break;
        if (taken.length === 0 && MODALS.has(w.toLowerCase()))
            return null;
        // A connective ends the label: "position OF all", "consecutive weeks OF".
        if (STOPWORDS.has(w.toLowerCase()))
            break;
        taken.push(w);
    }
    const label = taken.join(" ");
    return label.length > 0 && isUsableLabel(label) ? label : null;
}
/** Connectives at either end carry no name: "the role of" is `role`. */
function trimStopwords(label) {
    const words = label.split(/\s+/).filter((w) => w.length > 0);
    while (words.length > 0 && STOPWORDS.has(words[0].toLowerCase()))
        words.shift();
    while (words.length > 0 && STOPWORDS.has(words[words.length - 1].toLowerCase()))
        words.pop();
    return words.join(" ");
}
/** Punctuation that introduces a value rather than ending a label: the currency
 *  symbol in "Fixed Compensation: $______", the quotes in 'the role of "____"'.
 *  The label scan wants its words to end AT the gap, so any of these hid the
 *  label behind them — live, on tpl-1150c39b13ac293b, that left the fee itself
 *  unnamed, and an unnamed slot is one a reviewer removes. */
const BEFORE_VALUE = /["'“”«»$€£(\[\s]+$/;
/** The label immediately before `at`, or null when there is none worth using. */
function usableLabelBefore(text, at) {
    const window = text.slice(Math.max(0, at - 40), at);
    // The established path, returned exactly as it always was: `dated_as_of` and
    // `commencing_on` read better than `dated` and `commencing`, and a value finds
    // its slot BY NAME, so renaming what already works costs fills.
    const direct = FUZZY_LABEL.exec(window);
    if (direct !== null && isUsableLabel(direct[1]))
        return direct[1];
    // Look through the punctuation — but only for a label that reads as PROSE.
    // A document's own heading sits in the same place ('PERFORMER DEAL OFFER
    // MEMO "______"') and naming that gap `memo` would be worse than leaving it
    // unnamed, because a reviewer skims past a plausible name.
    const looked = FUZZY_LABEL.exec(window.replace(BEFORE_VALUE, ""));
    if (looked === null || !/[a-z]/.test(looked[1]) || !isUsableLabel(looked[1]))
        return null;
    const label = trimStopwords(looked[1]);
    return label.length > 0 ? label : null;
}
const CONTEXT_BEFORE = 190;
const CONTEXT_AFTER = 90;
/** Curly quotes are normalized so declaration matching is not defeated by
 *  typography. Callers pass the result of `htmlToText`. */
export function normalizeQuotes(text) {
    return text.replace(/[“”]/g, '"').replace(/[‘’]/g, "'");
}
/** A tab used as a blank, with tags and spaces allowed on either side. */
const TAB_BLANK = /([a-z])((?:<[^>]+>|[ ])*)\t+((?:<[^>]+>|[ ])*)(?=[a-z])/g;
/**
 * Rescue blanks the HTML→text extraction would destroy. Run on the RAW HTML,
 * before `htmlToText`.
 *
 * Some templates write a blank as a literal TAB rather than underscores:
 *
 *   ...principal photography [for up to \t consecutive weeks]...
 *
 * `htmlToText` collapses `[ \t]+` to a single space — correct for review
 * segmentation, and it is shared with review, classification and drafts, so it
 * is not the place to change. But it means the tagger receives "for up to
 * consecutive weeks", a sentence with no gap in it at all. No downstream rule
 * can recover a blank that no longer exists, which is why one correct value
 * ("14 weeks") kept having nowhere to land even after it was resolved from the
 * deal file.
 *
 * The rule is narrow because the corpus made it narrow. Measured across four
 * certified templates carrying 116-199 tabs each, tabs are overwhelmingly list
 * markers (`1.\tCONDITION(S) PRECEDENT`, `1.1\tCompany's approval`), and
 * EXACTLY ONE per template sits between two lowercase words — which in every
 * case is this blank. So: a tab between lowercase words becomes an underscore
 * run, and every other tab is left alone.
 */
export function rescueTabBlanks(html) {
    return html.replace(TAB_BLANK, (_m, before, pre, post) => `${before}${pre} ____ ${post}`);
}
/**
 * The one correct way to turn a stored template into the text the tagger works
 * against. Order matters and is easy to get wrong: the tab rescue must see the
 * RAW HTML, because htmlToText is what destroys the tab.
 */
export function templateTextForTagging(html) {
    return normalizeQuotes(htmlToText(rescueTabBlanks(html)));
}
export function toSlotId(raw) {
    return raw
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .slice(0, 44);
}
/** Editorial notes — drafting instructions, not contract language: every note
 *  the note convention reads (readers/notes.ts), conditionals and footnotes
 *  included. Returned separately from slots because deleting text and filling
 *  text are different operations; `kind` says which a note is (only a bracketed
 *  `note` is ever deleted — a conditional's wording stays). */
export function findEditorialNotes(text) {
    return drafterNotesIn(text).map((n) => ({ start: n.start, end: n.end, text: n.text, kind: n.kind }));
}
/** Phase 1. Deterministic slots, in document order, non-overlapping. */
export function tagDeterministically(text) {
    const found = [];
    for (const m of text.matchAll(BLANK_RE)) {
        const start = m.index ?? 0;
        const end = start + m[0].length;
        const decl = DECL_AFTER.exec(text.slice(end, end + 60));
        const fuzzy = usableLabelBefore(text, start) ?? usableLabelAfter(text, end);
        found.push({
            start,
            end,
            current: m[0],
            kind: "blank",
            phase: "deterministic",
            name: decl !== null ? decl[1] : (fuzzy ?? ""),
            rule: decl !== null
                ? `declaration "${decl[1]}"`
                : fuzzy !== null
                    ? `label "${fuzzy}"`
                    : "unnamed blank",
        });
    }
    for (const b of text.matchAll(BRACKET_RE)) {
        const at = b.index ?? 0;
        const span = b[0];
        if (opensAsNote(span))
            continue;
        if (LABEL_RE.test(span)) {
            found.push({
                start: at,
                end: at + span.length,
                current: span,
                kind: "blank",
                phase: "deterministic",
                name: span.slice(1, -1),
                rule: "all-caps label",
            });
            continue;
        }
        const g = GAP_IN_BRACKET.exec(span);
        // A bracket holding a full underscore run is already covered above; only a
        // gap the blank scan cannot see is added here.
        if (g === null || /_{3,}/.test(g[0]))
            continue;
        const start = at + (g.index ?? 0);
        found.push({
            start,
            end: start + g[0].length,
            current: g[0],
            kind: "blank",
            phase: "deterministic",
            name: usableLabelBefore(text, start) ?? usableLabelAfter(text, start + g[0].length) ?? "",
            rule: g[0].includes("\t") ? "tab gap in bracket" : "short underscore in bracket",
        });
    }
    for (const m of text.matchAll(MONEY_RE)) {
        const start = m.index ?? 0;
        // A money slot names itself from its own unit where it has one — "$100 per
        // diem" is the per-diem amount. Reading backwards instead finds the article
        // ("a $100 per diem") and yields nothing usable, which is how the real
        // §7.1.B case presents.
        const unit = /\s*per\s*(diem|day|week)$/i.exec(m[0]);
        const fuzzy = usableLabelBefore(text, start);
        const name = unit !== null ? `per_${unit[1].toLowerCase()}_amount` : fuzzy !== null ? `${fuzzy}_amount` : "";
        // The unit NAMES the slot but is not PART of it. Measured end to end: with
        // "$100 per diem" as the span, a filler told the slot is `per_diem_amount`
        // returns "125" — correct by its own lights — and the substitution reads
        // "a 125, and (c)...", having eaten the currency symbol and the words "per
        // diem" along with the figure. The amount is the variable; "per diem" is
        // contract language and has to survive.
        const span = unit === null ? m[0].length : m[0].length - unit[0].length;
        found.push({
            start,
            end: start + span,
            current: m[0].slice(0, span),
            kind: "default",
            phase: "deterministic",
            name,
            rule: unit !== null ? `money per ${unit[1].toLowerCase()}` : "money",
        });
    }
    return resolveOverlaps(found, text);
}
/** Overlaps are settled once, here — earliest start wins, longer span breaks a
 *  tie. Doing it in one place means no pass has to silently drop a collision,
 *  which is how earlier versions hid real conflicts instead of preventing them. */
export function resolveOverlaps(candidates, text) {
    const sorted = [...candidates].sort((a, b) => a.start - b.start || b.end - b.start - (a.end - a.start));
    const kept = [];
    let last = -1;
    for (const c of sorted) {
        if (c.start < last)
            continue;
        last = c.end;
        kept.push({
            id: toSlotId(c.name),
            start: c.start,
            end: c.end,
            current: c.current,
            kind: c.kind,
            phase: c.phase,
            rule: c.rule,
            context: text
                .slice(Math.max(0, c.start - CONTEXT_BEFORE), c.end + CONTEXT_AFTER)
                .replace(/\s+/g, " "),
        });
    }
    return kept;
}
// ── The same placeholders, read in a DRAFT ────────────────────────────────
// Two scans live here, each exactly as it was before the move (golden snapshot,
// 2026-10-18): the what's-left residue and, further down, find_draft_gaps.
// Drafter notes are NOT read here: they are readers/notes.ts's, the one note
// convention the tagger, the page and the summary all count by.
// Underscore blanks are not here: a signature line is a blank on purpose, and
// a deal term the draft did not carry is completeness (unplacedTerms), not
// residue. Mixed-case brackets are optional wording (C5).
const RESIDUE = [
    { form: "{{X}}", re: /\{\{[^{}]{1,80}\}\}/g },
    { form: "{X}", re: /(?<!\{)\{[A-Za-z_][\w .-]{0,40}\}(?!\})/g },
    { form: "<<X>>", re: /<<[^<>]{1,80}>>|«[^«»]{1,80}»/g },
    { form: "[ALL CAPS]", re: /\[(?=[^\]]*[A-Z]{2})[A-Z0-9 _/&'.,#-]{2,60}\]/g },
];
/** Every placeholder token left in the output, as written, and every drafter
 *  note (form `***note`, whatever its shape — readers/notes.ts). */
export function placeholderResidue(outputHtml) {
    const text = blocksOf(outputHtml).join("\n");
    const out = [];
    for (const { form, re } of RESIDUE)
        for (const m of text.matchAll(re))
            out.push({ form, token: m[0].trim() });
    for (const n of drafterNotesIn(text))
        out.push({ form: "***note", token: n.text.trim() });
    return out;
}
/** Any bracket or underscore blank — where a template leaves room for the deal
 *  (or a note). The defined-term aligner reads it as one BLANK token, and the
 *  generator's restore leaves a block holding one alone (moved from
 *  reviewEngineV2/definedTerms.ts and definedTermFidelity.ts). */
export const BLANK_OR_BRACKET_RE = /\[[^\]]*\]|_{3,}/;
/** A run of underscores, or the tab-blank a DOCX template leaves behind — a
 *  replacement still carrying one is template text, not a proposal (moved from
 *  services/recommendationSafety.ts). */
export const BLANK_OR_TAB_GAP_RE = /_{3,}|\t{2,}/;
/** The named slots a long-form template writes, each capturing its name (moved
 *  from services/LongFormWorkflowService.ts). */
export const NAMED_SLOT_PATTERNS = [
    /\[([^\]\n]{2,60})\]/g, // [TALENT NAME]
    /\{\{([^}\n]{2,60})\}\}/g, // {{compensation}}
    /<<([^>\n]{2,60})>>/g, // <<Effective Date>>
];
/** The generator's skeleton check (VertexDraftGenerator): a long blank run. */
const BLANK_RUN_RE = /_{4,}/g;
/** [scaffold-echo] What a template (or a draft) looks like unfilled: its blank
 *  fill-lines and its drafter notes (readers/notes.ts). */
export function skeletonCounts(html) {
    return { blanks: (html.match(BLANK_RUN_RE) ?? []).length, notes: drafterNotesIn(blocksOf(html).join("\n")).length };
}
/** What is actually left in a draft. Variables: on the slot path the approved
 *  slot map says it — slots minus the ones filled (an unfilled slot keeps the
 *  template's own text, which reads no different from a filled one); otherwise
 *  the placeholders still in the text. Notes: always the drafter notes the draft
 *  still carries, by the one note convention (readers/notes.ts) — the same one
 *  the review-variables page counts the template's notes by. */
export function whatsLeftIn(outputHtml, slotMap) {
    const residue = placeholderResidue(outputHtml);
    return {
        variablesLeft: slotMap !== undefined ? slotMap.variablesLeft : residue.filter((r) => r.form !== "***note").length,
        notesLeft: residue.filter((r) => r.form === "***note").length,
    };
}
export function draftWhatsLeft(finalHtml, fill) {
    const left = whatsLeftIn(finalHtml, fill === null ? undefined : { variablesLeft: fill.total - fill.filled });
    return { variablesFilled: fill?.filled ?? null, variables: fill?.total ?? null, ...left };
}
// ── find_draft_gaps (moved from services/draftGapScanner.ts) ──────────────
// A REAL tag never starts with a second `<` immediately after the first, nor
// does its name start with anything but a letter or `/` — but the <<Fee>>
// placeholder convention IS exactly a doubled `<`. A naive `/<[^>]+>/` would
// misparse "<<Fee>>" as the fake tag "<Fee>" (stripping it before the gap
// scan ever sees it) — the lookbehind rules that out on both the outer AND
// inner `<`, while still matching every real tag (p, h2, mark, etc.).
function stripTagsKeepingAngles(html) {
    return html.replace(/(?<!<)<\/?[a-zA-Z][^<>]*>/g, " ");
}
function collapse(s) {
    return s.replace(/\s+/g, " ").trim();
}
/** Block ends, so a paragraph still starts a line once the tags are gone (a
 *  footnote is a paragraph that opens with "*"). */
const BLOCK_END = /<\/(?:p|li|h[1-6]|td|th|div|blockquote)>|<br\s*\/?>/gi;
// Matches VertexDraftGenerator's own documented examples: {{Actor Name}},
// [Name], ____, <<Fee>>. Bounded length (1-80 chars inside the delimiters)
// so a stray/unmatched delimiter elsewhere in the document can't turn the
// rest of the draft into one giant "match."
const GAP_PATTERNS = [
    /\{\{[^{}]{1,80}\}\}/g,
    /\[[^[\]]{1,80}\]/g,
    /_{3,}/g,
    /<<[^<>]{1,80}>>/g,
];
const CONTEXT_RADIUS = 40;
const MAX_GAPS = 40;
/** Scans a saved draft's HTML for unfilled certified-template placeholder
 *  tokens and the drafter notes still in it. Returns matches in document
 *  order, capped at MAX_GAPS. An empty result means no known placeholder or
 *  note was found — NOT a guarantee the draft is complete (see
 *  services/draftGapScanner.ts). */
export function findDraftGaps(html) {
    const text = stripTagsKeepingAngles(html);
    // The same text with each block end read as a line break (one character for
    // one, so offsets agree): a footnote is a paragraph that opens with "*".
    const lines = stripTagsKeepingAngles(html.replace(BLOCK_END, "\u0001")).replace(/\u0001/g, "\n");
    // Drafter notes come from the one note reader; a placeholder inside a note
    // (a blank in "[***…most favored nations basis with ______]") is the note's.
    const notes = drafterNotesIn(lines).map((n) => ({ start: n.start, end: n.end, token: n.text, kind: n.kind }));
    const inNote = (at) => notes.some((n) => at >= n.start && at < n.end);
    const raw = [];
    for (const pattern of GAP_PATTERNS) {
        pattern.lastIndex = 0;
        let m;
        while ((m = pattern.exec(text)) !== null) {
            raw.push({ start: m.index, end: m.index + m[0].length, token: m[0], kind: "placeholder" });
        }
    }
    // A bracket/mustache/angle wrapper around a run of underscores (e.g.
    // "[____]") matches TWO patterns for the same blank — keep only the
    // outermost span per overlapping cluster so it's reported once.
    raw.sort((a, b) => a.start - b.start || b.end - a.end);
    const deduped = [];
    let lastEnd = -1;
    for (const m of raw) {
        if (m.start < lastEnd)
            continue;
        deduped.push(m);
        lastEnd = m.end;
    }
    // Placeholders and notes are capped apart, so a note-heavy draft cannot push
    // a real blank or [COMPANY NAME] out of the report.
    const placeholders = deduped.filter((m) => !inNote(m.start)).slice(0, MAX_GAPS);
    const found = [...placeholders, ...notes.slice(0, MAX_GAPS)].sort((a, b) => a.start - b.start);
    return found.map(({ start, end, token, kind }) => {
        const ctxStart = Math.max(0, start - CONTEXT_RADIUS);
        const ctxEnd = Math.min(text.length, end + CONTEXT_RADIUS);
        return {
            token: collapse(token),
            context: collapse(text.slice(ctxStart, ctxEnd)),
            kind,
        };
    });
}
// ── Certified language: the words between a template sentence's placeholders ──
// (moved from testing/documentConventions.ts, C1)
// Brackets are options (C5's business) and may span sentences; a blank, a
// placeholder or a drafter note marks a sentence the deal is meant to change.
const FILLABLE = /_{3,}|\{|\}|<<|>>|\[|\]|\*\*\*/;
const MIN_WORDS = 5;
/** The language of a template a draft must carry verbatim: each sentence, cut
 *  at its blanks, placeholders, options and drafter notes — the words between
 *  them are certified even when the sentence holds a value the deal sets
 *  ("commencing on ________, provided Company shall be entitled to…"). Whether
 *  an option was kept is C5's question, not this one's. */
export function certifiedSentencesOf(templateHtml) {
    const out = [];
    for (const raw of blocksOf(templateHtml)) {
        // A drafter note is never certified language, whatever its shape (readers/notes.ts).
        let block = "";
        let at = 0;
        for (const n of drafterNotesIn(raw)) {
            block += raw.slice(at, n.start) + "\u0000";
            at = n.end;
        }
        block += raw.slice(at);
        for (const sentence of block.split(/(?<=[.;:])\s+/)) {
            // Options nest ("[, excluding the key art [and/or magazine covers]]") and
            // the template leaves some unclosed: innermost first, then a stray
            // opener runs to the end of the sentence and a stray closer from its start.
            let cut = conventionText(sentence).replace(CLAUSE_NUMBER, "");
            for (let prev = ""; prev !== cut;)
                [prev, cut] = [cut, cut.replace(OPTION_SPAN, "\u0000")];
            cut = cut.replace(/\[.*$/, "\u0000").replace(/^.*\]/, "\u0000");
            for (const piece of cut.split(FILLABLE_SPLIT)) {
                const s = piece.trim().replace(/^[,;:)\]]\s*/, "").replace(/\s*[,;:([]$/, "").trim();
                if (s.split(" ").length < MIN_WORDS || FILLABLE.test(s))
                    continue;
                out.push(s);
            }
        }
    }
    return [...new Set(out)];
}
const FILLABLE_SPLIT = /\u0000|_{3,}|\{\{[^}]*\}\}|\{[^}]*\}|<<[^>]*>>|[[\]{}]|\*\*\*/;
//# sourceMappingURL=placeholders.js.map